import dynamic from "next/dynamic";
import { memo } from "react";

import { parseExperiences, singularBlock } from "@/lib/experiences";
import { BaseEntry, TargetSite } from "@/types";

import HeroFirstCard from "./HeroCarousel/HeroFirstCard";
import HeroProjectCommunityCard from "./HeroCarousel/HeroProjectCommunityCard";

// Dynamic import for the Experience wrapper to avoid SSR issues
const ExperienceWrapper = dynamic(() => import("./ExperienceWrapperProps"), {
  ssr: false,
  loading: () => <div>Loading...</div>, // Optional loading component
});

export const COMPONENTS: Record<string, any> = {
  socialMediaPresence: dynamic(() => import("./SocialMediaPresence/SocialMediaPresence")),
  heroEventLaunchCard: dynamic(() => import("./HeroCarousel/EventLaunchCard")),
  salesHomepageHeroLaunchCard: dynamic(() => import("./SalesHeroCarousel/SalesEventLaunchCard")),
  salesHomepageHeroLaunchCardEvent: dynamic(() => import("./SalesHeroCarousel/SalesEventLaunchCard")),
  homepageHero: dynamic(() => import("./HeroCarousel/HomePageHeroCarousel")),
  salesHeroBanner: dynamic(() => import("./SalesHeroCarousel/SalesHomePageHeroCarousel")),
  heroFirstCard: HeroFirstCard,
  salesHomepageHeroFirstCard: dynamic(() => import("./SalesHeroCarousel/SalesHeroFirstCard")),
  heroFirstCard_Corporate: dynamic(() => import("./HeroCarousel/HeroFirstCard_Corporate")),
  heroProjectCommunityCard: HeroProjectCommunityCard,
  salesHomepageHeroProjectcommunityCard: dynamic(() => import("./SalesHeroCarousel/SalesHeroProjectCommunityCard")),
  verticalCardSlideShow: dynamic(() => import("./VerticalCardSlideShow/VerticalCardSlideShow")),
  editorial1Col: dynamic(() => import("./UnitGallery/UnitEditorial1Col")),
  textBaseline: dynamic(() => import("./TextBaseline/TextBaseline")),
  editorial1Col_Corporate: dynamic(() => import("./UnitGallery_Corporate/UnitEditorial1Col")),
  textBaseline_Corporate: dynamic(() => import("./TextBaseline_Corporate/TextBaseline")),
  downloadMedia_Corporate: dynamic(() => import("./TextBaseline_Corporate/DownloadMedia")),
  dataGrid: dynamic(() => import("./TextBaseline/DataGrid")),
  downloadMedia: dynamic(() => import("./TextBaseline/DownloadMedia")),
  mediaBasic: dynamic(() => import("./Media/Media")),
  editorial2Col: dynamic(() => import("./Editorial2Col/Editorial2Col")),
  editorialModuleImage: dynamic(() => import("./Editorial2Col/EditorialModuleImage")),
  editorialModuleText: dynamic(() => import("./Editorial2Col/EditorialModuleText"), { ssr: false }),
  editorialModuleText2: dynamic(() => import("./Editorial2Col/EditorialModuleText2")),
  editorialModuleHighlights: dynamic(() => import("./Editorial2Col/EditorialModuleHighlights")),
  editorialModuleListIcon: dynamic(() => import("./Editorial2Col/EditorialModuleListIcon")),
  autoScrollTiles: dynamic(() => import("./AutoScrollTiles/AutoScrollTiles")),
  history: dynamic(() => import("./HistoryCarousel/HistoryCarousel")),
  assistanceModal: dynamic(() => import("./Checkout/CreateIntentLeadForm")),
  tabbedCarousel: dynamic(() => import("./TabGallery/TabGallery")),
  editorialFeatureList: dynamic(() => import("./FeatureList/FeatureList")),
  editorialFeatureList_Corporate: dynamic(() => import("./FeatureList_Corporate/FeatureList")),
  mediaBannerHighlight: dynamic(() => import("./MediaBannerHighlight/MediaBannerHighlight")),
  mediaBannerHighlight_Corporate: dynamic(() => import("./MediaBannerHighlight_Corporate/MediaBannerHighlight")),
  galleryCardCarousel: dynamic(() => import("./CardsGalleriesCarousel/CardsGalleriesCarousel")),
  cityLocation: dynamic(() => import("./CityLocation/CityLocation")),
  introInventory: dynamic(() => import("./InventoryLpIntro/InventoryLpIntro")),
  carouselTag: dynamic(() => import("./StorytellingCarousel/StorytellingCarousel")),
  editorialCarousel: dynamic(() => import("./AutogeneratedCarousel/AutogeneratedCarousel")),
  videoCarousel: dynamic(() => import("./AutogeneratedCarousel/AutogeneratedCarousel")),
  editorialVideoListing: dynamic(() => import("./VideoListing/VideoListing")),
  articleCarousel: dynamic(() => import("./AutogeneratedCarousel/ArticleCarouselWrapper")),
  highlightDualColumn: dynamic(() => import("./MediaHighlightDualColumn/MediaHighlightDualColumn")),
  editorial3ColumnCardList: dynamic(() => import("./EditorialThreeColumns/EditorialThreeColumns")),
  editorial3ColumnCardList_Corporate: dynamic(() => import("./EditorialThreeColumns_Corporate/EditorialThreeColumns")),
  editorialStickyBar: dynamic(() => import("./StickyBar/StickyBarEditorial")),
  articleList: dynamic(() => import("./ArticleListing/ArticleListingWrapper")),
  faqBox: dynamic(() => import("./FAQ/FAQ")),
  editorialInventory: dynamic(() => import("./EditorialInventory/EditorialInventory")),
  tileCarousel: dynamic(() => import("./TileCarousel/TileCarousel")),
  hubTemplateHero: dynamic(() => import("./HubTemplateHero/HubTemplateHero")),
  dHubIntro: dynamic(() => import("./DHubIntro/DHubIntroWrapper")),
  tabsComponent: dynamic(() => import("./TabNavigation/TabNavigation")),
  pageIntro: dynamic(() => import("./PageIntro/PageIntro")),
  heroBannerOos: dynamic(() => import("./OOSComponents/HeroBanner/HeroBanner")),
  buildingDocumentPageSection: dynamic(() => import("./OOSComponents/DocumentListing/DocumentListing")),
  singleColumnRichText: dynamic(() => import("./OOSComponents/SingleColumnRichText/SingleColumnRichText")),
  bentoList: dynamic(() => import("./BentoList/BentoList")),
  bentoList_Corporate: dynamic(() => import("./BentoList_Corporate/BentoList")),
  editorialTemplateHero: dynamic(() => import("./EditorialTemplateHero/EditorialTemplateHero")),
  breadcrumb: dynamic(() => import("./Breadcrumb/Breadcrumb")),
  editorial2Col_Corporate: dynamic(() => import("./EditorialDualColumn/EditorialDualColumn")),
  autogeneratedCarousel: dynamic(() => import("./AutogeneratedCarousel/AutogeneratedCarouselWrapper")),
  segmentedControl: dynamic(() => import("./SegmentedControl/SegmentedControl")),
  guidedToolLauncher: dynamic(() => import("./GuidedToolLauncher/GuidedToolLauncher")),
  contactServiced: dynamic(() => import("./ContactServices/ContactServices")),
  officeAddresses: dynamic(() => import("./OfficeAddress/OfficeAddress")),
  contactForm: dynamic(() => import("./ContactForm/ContactFormComponentClient"), { ssr: false }),
  documentCategoriesList: dynamic(() => import("./OOSComponents/DocumentCategories/DocumentCategories")),
  map: dynamic(() => import("./Map_Corporate/MapWrapper"), { ssr: false }),
  editorial1ColPropertyType3Button: dynamic(() => import("./TextBaseline/PropertyType3Buttons")),
  mortgageCalculator: dynamic(() => import("./MortgageCalculator/MortgageCalculatorForm"), { ssr: false }),
  anchor: dynamic(() => import("./UnitAnchors/UnitAnchors")),
  mcpEmptyDiv: dynamic(() => import("./EmptyDivMcp/EmptyDivMcp"), { ssr: false }),
  eventDescriptionSection: dynamic(() => import("./EventComponent/EventDescriptionSection")),
  comparisonTable: dynamic(() => import("./EventComponent/EventComparisonTable")),
};

// Component renderer for Ninetailed Experience
const ComponentRenderer = memo(({ dynamicComponent, ...props }: { dynamicComponent: any; [key: string]: any }) => {
  const Component = dynamicComponent;

  if (!Component) {
    return (
      <div className="flex flex-col gap-s justify-center h-full py-[80px] px-l items-stretch">
        <pre className="max-w-full overflow-auto bg-gray-7 text-black">{JSON.stringify(props, null, 2)}</pre>
      </div>
    );
  }

  return <Component {...props} />;
});

ComponentRenderer.displayName = "ComponentRenderer";

interface Props {
  components: BaseEntry[];
  target?: TargetSite;
  [key: string]: any;
  indexKey?: number;
  isEditorialPage?: boolean;
  isCheckoutPage?: boolean;
}

export default function DynamicImport({ components, target, indexKey, isEditorialPage, ...rest }: Props) {
  return components?.map((component, index) => {
    let corporateSuffix = "";
    if (target === TargetSite.corporate) {
      corporateSuffix = "_" + target;
    }

    const Component = COMPONENTS[component?.type + corporateSuffix] || COMPONENTS[component?.type];

    // Parse experiences for this component (safe type conversion)
    // const parsedExperiences = parseExperiences(component as unknown as singularBlock);

    // Extract key and component from rest to avoid conflicts
    const { key: restKey, component: restComponent, ...restProps } = rest;

    // Prepare props for the component
    const componentProps = {
      indexKey,
      firstComponent: isEditorialPage && component === components[0],
      entry: component,
      isInventoryAvaialable: rest.isInventoryAvaialable || "",
      typeIndex: component.typeIndex ?? "",
      ...restProps,
    };

    // If component has experiences, wrap with ExperienceWrapper
    // if (parsedExperiences.length > 0 && Component) {
    //   return (
    //     <ExperienceWrapper
    //       key={`experience-${component.id}`}
    //       id={component.id}
    //       component={() => <Component {...componentProps} />}
    //       experiences={parsedExperiences}
    //     />
    //   );
    // }

    // If no experiences, render component directly (server-side)
    if (Component) {
      return <Component key={component.id} {...componentProps} />;
    } else {
      return (
        <div key={component.id} className="flex flex-col gap-s justify-center h-full py-[80px] px-l items-stretch">
          <pre className="max-w-full overflow-auto bg-gray-7 text-black">{JSON.stringify(component, null, 2)}</pre>
        </div>
      );
    }
  });
}
